import { useMemo } from 'react';

// Cache for processed filter options to avoid recalculation
const filterCache = new Map();

/**
 * Hook to extract dynamic filter options from opportunities dataset
 * @param {Array} opportunities - Array of opportunity objects
 * @returns {Object} Filter options with counts and loading/error states
 */
const useDynamicOpportunityFilters = (opportunities = []) => {
  const filterOptions = useMemo(() => {
    if (!opportunities || opportunities.length === 0) {
      return {
        companyStages: [],
        roleAreas: [],
        experienceLevels: [],
        loading: false,
        error: null
      };
    }

    // Create cache key based on opportunities data
    const cacheKey = JSON.stringify(opportunities.map(opp => ({
      id: opp.id,
      fundingStage: opp.fundingStage,
      roleArea: opp.roleArea,
      experienceLevel: opp.experienceLevel
    })));

    // Check cache first
    if (filterCache.has(cacheKey)) {
      return filterCache.get(cacheKey);
    }

    try {
      // Extract unique company stages
      const stageMap = new Map();
      const roleAreaMap = new Map();
      const experienceLevelMap = new Map();

      opportunities.forEach(opportunity => {
        // Process funding stages
        if (opportunity.fundingStage && opportunity.fundingStage.trim()) {
          const stage = opportunity.fundingStage.trim();
          stageMap.set(stage, (stageMap.get(stage) || 0) + 1);
        }

        // Process role areas
        if (opportunity.roleArea && opportunity.roleArea.trim()) {
          const roleArea = opportunity.roleArea.trim();
          roleAreaMap.set(roleArea, (roleAreaMap.get(roleArea) || 0) + 1);
        }

        // Process experience levels
        if (opportunity.experienceLevel && opportunity.experienceLevel.trim()) {
          const experienceLevel = opportunity.experienceLevel.trim();
          experienceLevelMap.set(experienceLevel, (experienceLevelMap.get(experienceLevel) || 0) + 1);
        }
      });

      // Convert maps to sorted arrays with proper formatting
      const companyStages = Array.from(stageMap.entries())
        .map(([value, count]) => ({
          value: value.toLowerCase().replace(/[^a-z0-9]/g, '-'),
          label: formatStageLabel(value),
          count,
          color: getStageColor(value)
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

      const roleAreas = Array.from(roleAreaMap.entries())
        .map(([value, count]) => ({
          value: value.toLowerCase().replace(/[^a-z0-9]/g, '-'),
          label: formatRoleAreaLabel(value),
          count,
          color: getRoleAreaColor(value)
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

      const experienceLevels = Array.from(experienceLevelMap.entries())
        .map(([value, count]) => ({
          value: value.toLowerCase().replace(/[^a-z0-9]/g, '-'),
          label: formatExperienceLevelLabel(value),
          count,
          color: getExperienceLevelColor(value)
        }))
        .sort((a, b) => {
          // Sort experience levels in logical order
          const order = ['entry', 'mid', 'senior', 'executive'];
          const aIndex = order.findIndex(level => a.value.includes(level));
          const bIndex = order.findIndex(level => b.value.includes(level));
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          return b.count - a.count || a.label.localeCompare(b.label);
        });

      const result = {
        companyStages,
        roleAreas,
        experienceLevels,
        loading: false,
        error: null
      };

      // Cache the result
      filterCache.set(cacheKey, result);
      
      // Limit cache size to prevent memory leaks
      if (filterCache.size > 10) {
        const firstKey = filterCache.keys().next().value;
        filterCache.delete(firstKey);
      }

      return result;
    } catch (error) {
      console.error('Error processing opportunity filters:', error);
      return {
        companyStages: [],
        roleAreas: [],
        experienceLevels: [],
        loading: false,
        error: error.message || 'Failed to process filter options'
      };
    }
  }, [opportunities]);

  return filterOptions;
};

/**
 * Format stage labels for display
 */
const formatStageLabel = (stage) => {
  const stageMap = {
    'pre-seed': 'Pre-Seed',
    'preseed': 'Pre-Seed',
    'seed': 'Seed',
    'series-a': 'Series A',
    'series a': 'Series A',
    'seriesa': 'Series A',
    'series-b': 'Series B',
    'series b': 'Series B',
    'seriesb': 'Series B',
    'series-c': 'Series C+',
    'series c': 'Series C+',
    'seriesc': 'Series C+',
    'growth': 'Growth Stage',
    'growth stage': 'Growth Stage'
  };
  
  const normalized = stage.toLowerCase().trim();
  return stageMap[normalized] || stage;
};

/**
 * Format role area labels for display
 */
const formatRoleAreaLabel = (roleArea) => {
  const roleAreaMap = {
    'sales': 'Sales & Business Development',
    'business development': 'Sales & Business Development',
    'operations': 'Operations & Strategy',
    'strategy': 'Operations & Strategy',
    'technical': 'Engineering & Technical',
    'engineering': 'Engineering & Technical',
    'tech': 'Engineering & Technical',
    'marketing': 'Marketing & Growth',
    'growth': 'Marketing & Growth',
    'product': 'Product Management',
    'product management': 'Product Management',
    'finance': 'Finance & Accounting',
    'accounting': 'Finance & Accounting',
    'people': 'People & HR',
    'hr': 'People & HR',
    'human resources': 'People & HR',
    'design': 'Design & Creative',
    'creative': 'Design & Creative'
  };
  
  const normalized = roleArea.toLowerCase().trim();
  return roleAreaMap[normalized] || roleArea;
};

/**
 * Format experience level labels for display
 */
const formatExperienceLevelLabel = (experienceLevel) => {
  const experienceLevelMap = {
    'entry': '0-2 years',
    'entry level': '0-2 years',
    '0-2': '0-2 years',
    'junior': '0-2 years',
    'mid': '3-6 years',
    'mid level': '3-6 years',
    'mid-level': '3-6 years',
    '3-6': '3-6 years',
    'intermediate': '3-6 years',
    'senior': '7-10 years',
    'senior level': '7-10 years',
    '7-10': '7-10 years',
    'executive': '10+ years',
    'executive level': '10+ years',
    '10+': '10+ years',
    'leadership': '10+ years'
  };
  
  const normalized = experienceLevel.toLowerCase().trim();
  return experienceLevelMap[normalized] || experienceLevel;
};

/**
 * Get color for stage options
 */
const getStageColor = (stage) => {
  const stageColors = {
    'pre-seed': 'blue',
    'preseed': 'blue',
    'seed': 'green',
    'series-a': 'purple',
    'series a': 'purple',
    'seriesa': 'purple',
    'series-b': 'indigo',
    'series b': 'indigo',
    'seriesb': 'indigo',
    'series-c': 'pink',
    'series c': 'pink',
    'seriesc': 'pink',
    'growth': 'orange',
    'growth stage': 'orange'
  };
  
  const normalized = stage.toLowerCase().trim();
  return stageColors[normalized] || 'gray';
};

/**
 * Get color for role area options
 */
const getRoleAreaColor = (roleArea) => {
  const roleAreaColors = {
    'sales': 'green',
    'business development': 'green',
    'operations': 'blue',
    'strategy': 'blue',
    'technical': 'purple',
    'engineering': 'purple',
    'tech': 'purple',
    'marketing': 'pink',
    'growth': 'pink',
    'product': 'indigo',
    'product management': 'indigo',
    'finance': 'yellow',
    'accounting': 'yellow',
    'people': 'teal',
    'hr': 'teal',
    'human resources': 'teal',
    'design': 'orange',
    'creative': 'orange'
  };
  
  const normalized = roleArea.toLowerCase().trim();
  return roleAreaColors[normalized] || 'gray';
};

/**
 * Get color for experience level options
 */
const getExperienceLevelColor = (experienceLevel) => {
  const experienceLevelColors = {
    'entry': 'green',
    'entry level': 'green',
    '0-2': 'green',
    'junior': 'green',
    'mid': 'blue',
    'mid level': 'blue',
    'mid-level': 'blue',
    '3-6': 'blue',
    'intermediate': 'blue',
    'senior': 'purple',
    'senior level': 'purple',
    '7-10': 'purple',
    'executive': 'indigo',
    'executive level': 'indigo',
    '10+': 'indigo',
    'leadership': 'indigo'
  };
  
  const normalized = experienceLevel.toLowerCase().trim();
  return experienceLevelColors[normalized] || 'gray';
};

export default useDynamicOpportunityFilters;